import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select } from 'components/common';
import { deleteNavMenu, getAllAdminNavMenus } from 'apis/adminNavMenus';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const getLevelBadgeStyle = (level) => {
    switch (Number(level)) {
        case 1:
            return { backgroundColor: '#e0e7ff', color: '#3730a3' };
        case 2:
            return { backgroundColor: '#fef3c7', color: '#b45309' };
        case 3:
            return { backgroundColor: '#dcfce7', color: '#15803d' };
        case 4:
            return { backgroundColor: '#f3e8ff', color: '#7e22ce' };
        default:
            return { backgroundColor: '#e0f2fe', color: '#0369a1' };
    }
};

const NavMenusList = () => {
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCode, setLanguageCode] = useState('vi');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const { data: languages = [] } = useActiveLanguagesQuery();
    const queryParams = useMemo(() => ({
        languageCode,
        isActive: isActiveFilter === '' ? '' : isActiveFilter === 'true',
        keyword,
    }), [languageCode, keyword, isActiveFilter]);
    const { data = { items: [] }, isFetching, error } = useAdminListQuery('nav-menus', queryParams, getAllAdminNavMenus);
    const deleteMutation = useAdminDeleteMutation('nav-menus', deleteNavMenu);

    useEffect(() => { if (error) toast.error(error.message || 'T?i d? li?u th?t b?i'); }, [error, toast]);

    const mappedItems = data.items.map(item => ({
        ...item,
        id: item.Id || item.id,
        keyName: item.KeyName || item.keyName,
        title: item.Title || item.title,
        slug: item.Slug || item.slug,
        sortOrder: item.SortOrder || item.sortOrder,
        isActive: item.IsActive !== undefined ? item.IsActive : item.isActive,
        parentId: item.ParentId || item.parentId,
        level: item.Level ?? item.level ?? 1,
    }));

    const buildTree = (items) => {
        const map = {};
        const roots = [];
        items.forEach(item => { map[item.id] = { ...item, children: [] }; });
        items.forEach(item => {
            if (item.parentId && map[item.parentId]) map[item.parentId].children.push(map[item.id]);
            else roots.push(map[item.id]);
        });
        return roots;
    };

    const items = buildTree(mappedItems);

    // Flatten tree for table display
    const flatten = (nodes, level = 0) => {
        const result = [];
        for (const node of (nodes || [])) {
            result.push({ ...node, _level: level });
            if (node.children?.length) {
                result.push(...flatten(node.children, level + 1));
            }
        }
        return result;
    };

    const flatItems = flatten(items);

    const filtered = flatItems.filter(item => {
        const matchKeyword = !keyword || item.keyName?.toLowerCase().includes(keyword.toLowerCase());
        const matchActive = isActiveFilter === '' || String(item.isActive) === isActiveFilter;
        return matchKeyword && matchActive;
    });

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res && res.success) {
                toast.success('Xóa thành công');
            } else {
                toast.error(res?.message || 'Xóa thất bại');
            }
        } catch {
            toast.error('Xóa thất bại — vui lòng kiểm tra kết nối API');
        }
        setDeleteId(null);
    };

    return (
        <div className="admin-page">
            <PageHeader
                title="Menu Điều Hướng"
                subtitle="Quản lý cấu trúc menu điều hướng"
                action={<Button onClick={() => navigate('/admin/nav-menus/create')}>+ Thêm Menu</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item">
                    <Input
                        placeholder="Tìm kiếm theo từ khóa..."
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={isActiveFilter}
                        onChange={e => setIsActiveFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả trạng thái' },
                            { value: 'true', label: 'Hoạt động' },
                            { value: 'false', label: 'Không hoạt động' },
                        ]}
                    />
                </div>
                <div className="filter-item">
                    <Select
                        value={languageCode}
                        onChange={e => setLanguageCode(e.target.value)}
                        options={
                            languages.length > 0
                                ? languages.map(l => ({ value: l.code, label: l.name }))
                                : [
                                    { value: 'vi', label: 'Tiếng Việt' },
                                    { value: 'en', label: 'English' },
                                    { value: 'jp', label: 'Japanese' },
                                ]
                        }
                    />
                </div>
            </div>
            <div className="table-wrapper">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            {/* <th>Tên Key</th> */}
                            <th>Tiêu đề</th>
                            <th>Slug</th>
                            {/* <th>Menu Cha</th> */}
                            <th>Cấp (Level)</th>
                            <th>Thứ tự</th>
                            <th>Trạng thái</th>
                            <th>Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isFetching ? (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '48px', color: '#9ca3af' }}>Đang tải...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '48px', color: '#9ca3af' }}>Không tìm thấy menu nào</td></tr>
                        ) : (
                            filtered.map(item => (
                                <tr key={item.id}>
                                    <td style={{ color: '#9ca3af', fontSize: 13 }}>{item.id}</td>
                                    <td>
                                        {item._level > 0 && (
                                            <span style={{ color: '#9ca3af', marginRight: 6 }}>
                                                {'└─'.padStart(item._level * 2, '─')}
                                            </span>
                                        )}
                                        {item.title}
                                    </td>
                                    <td style={{ color: '#9ca3af' }}>{item.slug}</td>
                                    {/* <td style={{ color: '#9ca3af' }}>{item.parentId || <span style={{ color: '#9ca3af' }}>—</span>}</td> */}
                                    <td>
                                        <span style={{
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            display: 'inline-block',
                                            ...getLevelBadgeStyle(item.level)
                                        }}>
                                            Level {item.level || 1}
                                        </span>
                                    </td>
                                    <td>{item.sortOrder}</td>
                                    <td>
                                        {item.isActive !== undefined ? (
                                            <span className={`status-badge ${item.isActive ? 'active' : 'inactive'}`}>
                                                {item.isActive ? 'Hoạt động' : 'Không hoạt động'}
                                            </span>
                                        ) : (
                                            <span style={{ color: '#9ca3af' }}>—</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/nav-menus/edit/${item.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                                            <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(item.id)} title="Xóa"><FiTrash2 size={15} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <ConfirmationDialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Xóa Menu"
                message="Bạn có chắc chắn muốn xóa menu này không? Điều này có thể ảnh hưởng đến điều hướng trang web."
            />
        </div>
    );
};

export default NavMenusList;
